<template>
  <div class="panel-body">
    <HeaderGeneral :vista="vista" :ruta="proveniente" :carpeta="carpeta" />

    <!-- Búsqueda -->
    <b-card class="mb-3 filtros-card">
      <b-row>
        <b-col md="3">
          <b-form-group label="Número de solicitud">
            <b-form-input
              v-model="idBuscado"
              placeholder="Ej: 1023"
              type="number"
              min="1"
              @keyup.enter="buscarSolicitud"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Documento del paciente">
            <b-form-input
              v-model="documentoBuscado"
              placeholder="Número de documento..."
              @keyup.enter="buscarSolicitud"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Estado">
            <b-form-select v-model="estadoBuscado" :options="estadosOptions" />
          </b-form-group>
        </b-col>
        <b-col md="2" class="d-flex align-items-end pb-3">
          <b-button variant="primary" @click="buscarSolicitud" class="mr-2">
            <b-icon icon="search" /> Buscar
          </b-button>
          <b-button variant="outline-secondary" @click="limpiar">
            <b-icon icon="x" />
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla -->
    <b-table
      responsive
      id="tabla-gestion"
      head-variant="light"
      striped
      :items="solicitudes"
      :fields="fields"
      :busy="cargando"
      show-empty
      empty-text="No se encontraron solicitudes. Ingrese un criterio de búsqueda."
    >
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner variant="primary" />
        </div>
      </template>

      <template #cell(estado)="row">
        <b-badge :variant="variantEstado(row.item.estado_id)" class="badge-estado-negro">
          {{ row.item.estado }}
        </b-badge>
      </template>

      <template #cell(acciones)="row">
        <b-button
          size="sm"
          variant="warning"
          class="mr-1"
          v-b-tooltip.hover
          title="Cambiar estado"
          @click="abrirModalEstado(row.item)"
        >
          <b-icon icon="arrow-repeat" />
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          v-b-tooltip.hover
          title="Reasignar proveedor"
          @click="abrirModalProveedor(row.item)"
        >
          <b-icon icon="person-check-fill" />
        </b-button>
        <!-- <b-button
          size="sm"
          variant="info"
          class="ml-1"
          v-b-tooltip.hover
          title="Ver / Editar detalle"
          @click="abrirModalDetalle(row.item)"
        >
          <b-icon icon="pencil-square" />
        </b-button> -->
      </template>
    </b-table>

    <div class="divnodata" v-if="solicitudes.length === 0 && !cargando && buscado">
      <b-img class="bannernodata" src="/img/banner.jpg" />
      <p>No se encontraron solicitudes con los criterios indicados</p>
    </div>

    <!-- Paginación -->
    <b-row class="mt-2" v-if="total > 0">
      <b-col md="2" offset-md="8" class="text-right">
        <label>Registros por página:</label>
      </b-col>
      <b-col md="2">
        <b-form-select
          v-model="size"
          :options="[10, 20, 50]"
          @change="buscarSolicitud"
        />
      </b-col>
    </b-row>
    <b-pagination
      v-if="total > 0"
      class="justify-content-center mt-2"
      v-model="page"
      :total-rows="total"
      :per-page="size"
      @change="onPageChange"
    />

    <!-- Modal: Cambiar Estado -->
    <b-modal
      id="modal-cambiar-estado"
      v-model="showModalEstado"
      title="Cambiar estado de la solicitud"
      size="lg"
      @ok="confirmarCambioEstado"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      :ok-disabled="guardandoEstado || !nuevoEstado"
    >
      <div v-if="solicitudSeleccionada">
        <b-alert show variant="info" class="mb-3">
          <strong>Solicitud #{{ solicitudSeleccionada.id }}</strong> —
          {{ solicitudSeleccionada.paciente }} |
          Estado actual: <strong>{{ solicitudSeleccionada.estado }}</strong>
        </b-alert>

        <b-form-group label="Nuevo estado *">
          <b-form-select
            v-model="nuevoEstado"
            :options="estadosCambioOptions"
          />
        </b-form-group>

        <b-form-group label="Observaciones">
          <b-form-textarea
            v-model="observacionesEstado"
            rows="3"
            placeholder="Motivo o comentario del cambio de estado..."
          />
        </b-form-group>

        <b-overlay :show="guardandoEstado" rounded="sm">
          <div class="text-muted small">
            <b-icon icon="exclamation-triangle" variant="warning" /> Este cambio se verá reflejado de inmediato en el historial de la solicitud.
          </div>
        </b-overlay>
      </div>
    </b-modal>

    <!-- Modal: Reasignar Proveedor -->
    <b-modal
      id="modal-reasignar-proveedor"
      v-model="showModalProveedor"
      title="Reasignar proveedor"
      size="lg"
      @ok="confirmarReasignacion"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      :ok-disabled="guardandoProveedor || !nuevoProveedorId"
    >
      <div v-if="solicitudSeleccionada">
        <b-alert show variant="info" class="mb-3">
          <strong>Solicitud #{{ solicitudSeleccionada.id }}</strong> —
          {{ solicitudSeleccionada.paciente }} |
          Proveedor actual: <strong>{{ solicitudSeleccionada.proveedor || 'Sin asignar' }}</strong>
        </b-alert>

        <b-form-group label="Nuevo proveedor *">
          <b-form-select
            v-model="nuevoProveedorId"
            :options="proveedoresOptions"
            :disabled="cargandoProveedores"
          />
          <b-spinner v-if="cargandoProveedores" small variant="primary" class="mt-1" />
        </b-form-group>

        <b-form-group label="Observaciones">
          <b-form-textarea
            v-model="observacionesProveedor"
            rows="3"
            placeholder="Motivo de la reasignación..."
          />
        </b-form-group>

        <b-overlay :show="guardandoProveedor" rounded="sm">
          <div class="text-muted small">
            <b-icon icon="exclamation-triangle" variant="warning" /> La reasignación actualizará el proveedor y registrará la observación en el historial.
          </div>
        </b-overlay>
      </div>
    </b-modal>

    <!-- Modal: Editar solicitud completa -->
    <b-modal
      id="modal-detalle-solicitud"
      v-model="showModalDetalle"
      title="Editar solicitud (Administrador)"
      size="xl"
      scrollable
      hide-footer
      @hidden="cerrarModalDetalle"
    >
      <div v-if="cargandoDetalle" class="text-center py-5">
        <b-spinner variant="primary" style="width:3rem;height:3rem;" />
        <p class="mt-3">Cargando datos de la solicitud...</p>
      </div>

      <div v-else-if="formDetalle">
        <b-alert show variant="info" class="mb-3">
          <b-icon icon="lock-fill" /> Activa el candado de cada campo que quieras modificar. El resto se mantendrá igual al valor original.
          <span v-if="camposActivadosCount > 0" class="ml-2">
            <b-badge variant="success">{{ camposActivadosCount }} campo{{ camposActivadosCount > 1 ? 's' : '' }} seleccionado{{ camposActivadosCount > 1 ? 's' : '' }}</b-badge>
          </span>
        </b-alert>

        <!-- Paciente -->
        <b-card class="mb-3" header-bg-variant="primary" header-text-variant="white">
          <template #header><strong><b-icon icon="person-fill" /> Datos del Paciente</strong></template>
          <b-row>
            <b-col md="6">
              <b-form-group label="Nombre completo">
                <b-input-group>
                  <b-form-input v-model="formDetalle.nombre_completo" :disabled="!activados.nombre_completo" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.nombre_completo ? 'success' : 'outline-secondary'" @click="toggleCampo('nombre_completo')"><b-icon :icon="activados.nombre_completo ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Tipo documento">
                <b-input-group>
                  <b-form-select v-model="formDetalle.tipo_documento" :options="listaTiposDocumento" value-field="id" text-field="name" :disabled="!activados.tipo_documento" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.tipo_documento ? 'success' : 'outline-secondary'" @click="toggleCampo('tipo_documento')"><b-icon :icon="activados.tipo_documento ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Número documento">
                <b-input-group>
                  <b-form-input v-model="formDetalle.documento" type="number" :disabled="!activados.documento" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.documento ? 'success' : 'outline-secondary'" @click="toggleCampo('documento')"><b-icon :icon="activados.documento ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Fecha nacimiento">
                <b-input-group>
                  <b-form-input v-model="formDetalle.nacimiento" type="date" :disabled="!activados.nacimiento" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.nacimiento ? 'success' : 'outline-secondary'" @click="toggleCampo('nacimiento')"><b-icon :icon="activados.nacimiento ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Género">
                <b-input-group>
                  <b-form-select v-model="formDetalle.genero" :options="listaGenero" value-field="id" text-field="name" :disabled="!activados.genero" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.genero ? 'success' : 'outline-secondary'" @click="toggleCampo('genero')"><b-icon :icon="activados.genero ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Talla">
                <b-input-group>
                  <b-form-select v-model="formDetalle.talla" :options="listaTalla" value-field="id" text-field="name" :disabled="!activados.talla" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.talla ? 'success' : 'outline-secondary'" @click="toggleCampo('talla')"><b-icon :icon="activados.talla ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Nacionalidad">
                <b-input-group>
                  <b-form-input v-model="formDetalle.nacionalidad" :disabled="!activados.nacionalidad" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.nacionalidad ? 'success' : 'outline-secondary'" @click="toggleCampo('nacionalidad')"><b-icon :icon="activados.nacionalidad ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Teléfono 1">
                <b-input-group>
                  <b-form-input v-model="formDetalle.telefono_1" type="number" :disabled="!activados.telefono_1" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.telefono_1 ? 'success' : 'outline-secondary'" @click="toggleCampo('telefono_1')"><b-icon :icon="activados.telefono_1 ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Teléfono 2">
                <b-input-group>
                  <b-form-input v-model="formDetalle.telefono_2" type="number" :disabled="!activados.telefono_2" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.telefono_2 ? 'success' : 'outline-secondary'" @click="toggleCampo('telefono_2')"><b-icon :icon="activados.telefono_2 ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Email">
                <b-input-group>
                  <b-form-input v-model="formDetalle.email" type="email" :disabled="!activados.email" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.email ? 'success' : 'outline-secondary'" @click="toggleCampo('email')"><b-icon :icon="activados.email ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="EPS">
                <b-input-group>
                  <b-form-select v-model="formDetalle.eps" :options="listaEps" value-field="id" text-field="name" :disabled="!activados.eps" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.eps ? 'success' : 'outline-secondary'" @click="toggleCampo('eps')"><b-icon :icon="activados.eps ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Cobertura">
                <b-input-group>
                  <b-form-select v-model="formDetalle.cobertura" :options="listaCobertura" value-field="id" text-field="name" :disabled="!activados.cobertura" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.cobertura ? 'success' : 'outline-secondary'" @click="toggleCampo('cobertura')"><b-icon :icon="activados.cobertura ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Afiliación">
                <b-input-group>
                  <b-form-select v-model="formDetalle.afiliacion" :options="listaAfiliacion" value-field="id" text-field="name" :disabled="!activados.afiliacion" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.afiliacion ? 'success' : 'outline-secondary'" @click="toggleCampo('afiliacion')"><b-icon :icon="activados.afiliacion ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Tipo paciente">
                <b-input-group>
                  <b-form-input v-model="formDetalle.tipo_paciente" :disabled="!activados.tipo_paciente" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.tipo_paciente ? 'success' : 'outline-secondary'" @click="toggleCampo('tipo_paciente')"><b-icon :icon="activados.tipo_paciente ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Nivel de ingreso">
                <b-input-group>
                  <b-form-input v-model="formDetalle.nivel_ingreso" :disabled="!activados.nivel_ingreso" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.nivel_ingreso ? 'success' : 'outline-secondary'" @click="toggleCampo('nivel_ingreso')"><b-icon :icon="activados.nivel_ingreso ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="IPS del paciente">
                <b-input-group>
                  <b-form-input v-model="formDetalle.ips_paciente" :disabled="!activados.ips_paciente" class="text-uppercase" @input="formDetalle.ips_paciente = $event.toUpperCase()" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.ips_paciente ? 'success' : 'outline-secondary'" @click="toggleCampo('ips_paciente')"><b-icon :icon="activados.ips_paciente ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Ubicación -->
        <b-card class="mb-3" header-bg-variant="secondary" header-text-variant="white">
          <template #header><strong><b-icon icon="geo-alt-fill" /> Ubicación</strong></template>
          <b-row>
            <b-col md="4">
              <b-form-group label="Departamento">
                <b-input-group>
                  <b-form-input v-model="formDetalle.departamento" :disabled="!activados.departamento" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.departamento ? 'success' : 'outline-secondary'" @click="toggleCampo('departamento')"><b-icon :icon="activados.departamento ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Municipio">
                <b-input-group>
                  <b-form-input v-model="formDetalle.municipio" :disabled="!activados.municipio" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.municipio ? 'success' : 'outline-secondary'" @click="toggleCampo('municipio')"><b-icon :icon="activados.municipio ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Barrio">
                <b-input-group>
                  <b-form-input v-model="formDetalle.barrio" :disabled="!activados.barrio" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.barrio ? 'success' : 'outline-secondary'" @click="toggleCampo('barrio')"><b-icon :icon="activados.barrio ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Dirección completa">
                <b-input-group>
                  <b-form-input v-model="formDetalle.direccion" :disabled="!activados.direccion" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.direccion ? 'success' : 'outline-secondary'" @click="toggleCampo('direccion')"><b-icon :icon="activados.direccion ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Datos clínicos -->
        <b-card class="mb-3" header-bg-variant="success" header-text-variant="white">
          <template #header><strong><b-icon icon="heart-pulse-fill" /> Datos Clínicos</strong></template>
          <b-row>
            <b-col md="6">
              <b-form-group label="Diagnóstico">
                <b-input-group>
                  <b-form-input v-model="formDetalle.diagnostico" :disabled="!activados.diagnostico" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.diagnostico ? 'success' : 'outline-secondary'" @click="toggleCampo('diagnostico')"><b-icon :icon="activados.diagnostico ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="CIE-10">
                <b-input-group>
                  <b-form-input v-model="formDetalle.cie_10_diagnostico" :disabled="!activados.cie_10_diagnostico" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.cie_10_diagnostico ? 'success' : 'outline-secondary'" @click="toggleCampo('cie_10_diagnostico')"><b-icon :icon="activados.cie_10_diagnostico ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Dosis">
                <b-input-group>
                  <b-form-input v-model="formDetalle.dosis" :disabled="!activados.dosis" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.dosis ? 'success' : 'outline-secondary'" @click="toggleCampo('dosis')"><b-icon :icon="activados.dosis ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Horas consumo/día">
                <b-input-group>
                  <b-form-input v-model="formDetalle.horas_consumo_dia" :disabled="!activados.horas_consumo_dia" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.horas_consumo_dia ? 'success' : 'outline-secondary'" @click="toggleCampo('horas_consumo_dia')"><b-icon :icon="activados.horas_consumo_dia ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Tiempo revisión médica (días)">
                <b-input-group>
                  <b-form-input v-model="formDetalle.tiempo_revision_medica" type="number" :disabled="!activados.tiempo_revision_medica" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.tiempo_revision_medica ? 'success' : 'outline-secondary'" @click="toggleCampo('tiempo_revision_medica')"><b-icon :icon="activados.tiempo_revision_medica ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Médico tratante">
                <b-input-group>
                  <b-form-input v-model="formDetalle.doctor" :disabled="!activados.doctor" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.doctor ? 'success' : 'outline-secondary'" @click="toggleCampo('doctor')"><b-icon :icon="activados.doctor ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Lugar entrega">
                <b-input-group>
                  <b-form-select v-model="formDetalle.lugar_entrega" :options="listaLugar" value-field="id" text-field="name" :disabled="!activados.lugar_entrega" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.lugar_entrega ? 'success' : 'outline-secondary'" @click="toggleCampo('lugar_entrega')"><b-icon :icon="activados.lugar_entrega ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Tipo máscara">
                <b-input-group>
                  <b-form-select v-model="formDetalle.tipo_mascara" :options="listaMascara" value-field="id" text-field="name" :disabled="!activados.tipo_mascara" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.tipo_mascara ? 'success' : 'outline-secondary'" @click="toggleCampo('tipo_mascara')"><b-icon :icon="activados.tipo_mascara ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hora dosis">
                <b-input-group>
                  <b-form-input v-model="formDetalle.hora_dosis" type="number" min="0" max="23" :disabled="!activados.hora_dosis" />
                  <b-input-group-append>
                    <b-button size="sm" :variant="activados.hora_dosis ? 'success' : 'outline-secondary'" @click="toggleCampo('hora_dosis')"><b-icon :icon="activados.hora_dosis ? 'unlock-fill' : 'lock-fill'" /></b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Observaciones y motivo -->
        <b-card class="mb-3">
          <template #header><strong><b-icon icon="chat-text-fill" /> Observaciones</strong></template>
          <b-form-group label="Observaciones de la solicitud">
            <b-input-group>
              <b-form-textarea v-model="formDetalle.observaciones" rows="2" :disabled="!activados.observaciones" />
              <b-input-group-append>
                <b-button size="sm" :variant="activados.observaciones ? 'success' : 'outline-secondary'" @click="toggleCampo('observaciones')"><b-icon :icon="activados.observaciones ? 'unlock-fill' : 'lock-fill'" /></b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group label="Motivo de la modificación *">
            <b-form-textarea
              v-model="observacionDetalle"
              rows="2"
              placeholder="Explique el motivo de esta edición administrativa..."
              :state="observacionDetalle.trim().length > 0 ? true : null"
            />
            <b-form-invalid-feedback>El motivo es obligatorio</b-form-invalid-feedback>
          </b-form-group>
        </b-card>

        <b-row class="mb-3">
          <b-col cols="auto" class="ml-auto">
            <b-button variant="secondary" class="mr-2" @click="cerrarModalDetalle">Cancelar</b-button>
            <b-button
              variant="success"
              :disabled="guardandoDetalle || observacionDetalle.trim().length === 0 || camposActivadosCount === 0"
              @click="guardarDetalle"
            >
              <b-spinner small v-if="guardandoDetalle" class="mr-1" />
              {{ guardandoDetalle ? 'Guardando...' : `Guardar ${camposActivadosCount} campo${camposActivadosCount !== 1 ? 's' : ''}` }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { RepositoryFactory } from "../../repositories/RepositoryFactory";
import HeaderGeneral from "../../components/HeaderGeneral.vue";

const configRepo = RepositoryFactory.get("config");
const clientesRepo = RepositoryFactory.get("clientes");

const ESTADOS_MAP = {
  1: "Nuevo",
  2: "Devuelto",
  3: "En solicitud",
  5: "Solicitud tomada",
  6: "Proceso de entrega",
  7: "Entregado",
  8: "Fallo de entrega",
  9: "Retiro solicitado",
  10: "Finalizado",
  11: "Cancelado",
  12: "Segundo intento de entrega",
  13: "Retiro tomado",
  14: "Retiro en proceso",
  15: "Retiro entregado",
  16: "Retiro fallido",
  17: "Dividida",
};

const RELACIONES = [
  {
    entidad: {
      nombre: "listados",
      columna_relacion: { rows: "id" },
      columna_relacion_principal: "estado",
      order: { column: "col", type: "asc" },
      size: 10,
      page: 1,
      where: [{ AND: [{ nombre_lista: { iLike: "estados" } }] }],
    },
  },
  {
    entidad: {
      nombre: "entidad_proveedor",
      columna_relacion: "id",
      columna_relacion_principal: "id_proveedor",
      order: { column: "col", type: "asc" },
      size: 5,
      page: 1,
      where: [{ AND: [{ id: { iLike: "" } }] }],
    },
  },
  {
    entidad: {
      nombre: "entidad_cliente",
      columna_relacion: "id",
      columna_relacion_principal: "id_cliente",
      order: { column: "col", type: "asc" },
      size: 5,
      page: 1,
      where: [{ AND: [{ id: { iLike: "" } }] }],
    },
  },
];

export default {
  components: { HeaderGeneral },

  computed: {
    ESTADOS_MAP() {
      return ESTADOS_MAP;
    },
    camposActivadosCount() {
      return Object.values(this.activados).filter(Boolean).length;
    },
  },

  data() {
    return {
      vista: "Gestión de Solicitudes",
      proveniente: " - Configuración",
      carpeta: "Configuración",

      // Filtros de búsqueda
      idBuscado: "",
      documentoBuscado: "",
      estadoBuscado: null,
      buscado: false,

      // Paginación
      page: 1,
      size: 20,
      total: 0,

      // Tabla
      solicitudes: [],
      cargando: false,

      fields: [
        { key: "id", label: "N° Solicitud", sortable: false, thStyle: { width: "110px" } },
        { key: "fecha_solicitud", label: "Fecha solicitud", sortable: false },
        { key: "paciente", label: "Paciente", sortable: false },
        { key: "documento", label: "Documento", sortable: false },
        { key: "solicitante", label: "Solicitante (Cliente)", sortable: false },
        { key: "proveedor", label: "Proveedor", sortable: false },
        { key: "estado", label: "Estado", sortable: false },
        { key: "acciones", label: "Acciones", sortable: false, thStyle: { width: "110px" } },
      ],

      estadosOptions: [
        { value: null, text: "Todos los estados" },
        { value: 1, text: "Nuevo" },
        { value: 2, text: "Devuelto" },
        { value: 3, text: "En solicitud" },
        { value: 5, text: "Solicitud tomada" },
        { value: 6, text: "Proceso de entrega" },
        { value: 7, text: "Entregado" },
        { value: 8, text: "Fallo de entrega" },
        { value: 9, text: "Retiro solicitado" },
        { value: 10, text: "Finalizado" },
        { value: 11, text: "Cancelado" },
        { value: 12, text: "Segundo intento de entrega" },
        { value: 13, text: "Retiro tomado" },
        { value: 17, text: "Dividida" },
      ],

      estadosCambioOptions: [
        { value: null, text: "Seleccionar nuevo estado..." },
        { value: 1, text: "1 - Nuevo" },
        { value: 2, text: "2 - Devuelto" },
        { value: 3, text: "3 - En solicitud" },
        { value: 5, text: "5 - Solicitud tomada" },
        { value: 6, text: "6 - Proceso de entrega" },
        { value: 7, text: "7 - Entregado" },
        { value: 8, text: "8 - Fallo de entrega" },
        { value: 9, text: "9 - Retiro solicitado" },
        { value: 10, text: "10 - Finalizado" },
        { value: 11, text: "11 - Cancelado" },
        { value: 12, text: "12 - Segundo intento de entrega" },
        { value: 13, text: "13 - Retiro tomado" },
        { value: 14, text: "14 - Retiro en proceso" },
        { value: 15, text: "15 - Retiro entregado" },
        { value: 16, text: "16 - Retiro fallido" },
        { value: 17, text: "17 - Dividida" },
      ],

      // Modal estado
      showModalEstado: false,
      solicitudSeleccionada: null,
      nuevoEstado: null,
      observacionesEstado: "",
      guardandoEstado: false,

      // Modal proveedor
      showModalProveedor: false,
      nuevoProveedorId: null,
      observacionesProveedor: "",
      guardandoProveedor: false,
      proveedoresOptions: [{ value: null, text: "Seleccionar proveedor..." }],
      cargandoProveedores: false,

      // Modal detalle / edición
      showModalDetalle: false,
      cargandoDetalle: false,
      guardandoDetalle: false,
      formDetalle: null,
      formDetalleOriginal: null,
      activados: {},
      observacionDetalle: "",
      // Listas para el modal de edición
      listaTiposDocumento: [],
      listaGenero: [],
      listaTalla: [],
      listaEps: [],
      listaCobertura: [],
      listaAfiliacion: [],
      listaLugar: [],
      listaMascara: [],
    };
  },

  methods: {
    async buscarSolicitud() {
      this.cargando = true;
      this.buscado = true;
      this.page = 1;
      try {
        const { solicitudes, total } = await this.fetchSolicitudes();
        this.solicitudes = solicitudes;
        this.total = total;
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al buscar solicitudes", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.cargando = false;
      }
    },

    async onPageChange(page) {
      this.page = page;
      this.cargando = true;
      try {
        const { solicitudes, total } = await this.fetchSolicitudes();
        this.solicitudes = solicitudes;
        this.total = total;
      } catch (err) {
        console.error(err);
      } finally {
        this.cargando = false;
      }
    },

    async fetchSolicitudes() {
      const where = this.buildWhere();
      const params = {
        nombreEntidad: "entidad_solicitud",
        idVersion: 2,
        column: "id",
        type: "desc",
        size: this.size,
        page: this.page,
        typeOrder: "desc",
        filterByColum: "",
        filter: "",
        dateBy: "createat",
        startDate: "",
        endDate: "",
      };

      const response = await configRepo.getMetaData(params, where, RELACIONES);

      if (!response || !response.message || response.message === "Error no se encontró datos") {
        return { solicitudes: [], total: 0 };
      }

      const rows = response.message.rows || [];
      const total = response.message.count || 0;

      const solicitudes = rows.map((row) => {
        const d = row.data;

        const estadoBuscado = (d.listados || [])
          .flat()
          .find(
            (item) =>
              item.row_data &&
              item.row_data.id === d.estado &&
              item.nombre_lista === "estados"
          );
        const estadoNombre = estadoBuscado
          ? estadoBuscado.row_data.name
          : ESTADOS_MAP[d.estado] || String(d.estado || "-");

        const nombreCliente = d.entidad_cliente?.[0]?.[0]?.d?.nombre_cliente || "-";
        const nombreProveedor = d.entidad_proveedor?.[0]?.[0]?.d?.nombre_proveedor || "Sin asignar";

        return {
          id: d.id,
          id_metadata: d.id,
          fecha_solicitud: d.createat
            ? new Date(d.createat).toLocaleString("es-CO", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
          paciente: d.nombre_completo || "-",
          documento: d.documento || "-",
          solicitante: nombreCliente,
          proveedor: nombreProveedor,
          estado: estadoNombre,
          estado_id: d.estado,
          id_proveedor: d.id_proveedor,
        };
      });

      return { solicitudes, total };
    },

    buildWhere() {
      const conditions = [];

      if (this.idBuscado && String(this.idBuscado).trim() !== "") {
        conditions.push({ id: { equals: parseInt(this.idBuscado) } });
      }

      if (this.documentoBuscado && this.documentoBuscado.trim() !== "") {
        conditions.push({ documento: { iLike: this.documentoBuscado.trim() } });
      }

      if (this.estadoBuscado !== null && this.estadoBuscado !== undefined) {
        conditions.push({ estado: { equals: this.estadoBuscado } });
      }

      if (conditions.length === 0) {
        return [{ AND: [{ id: { greaterThan: 0 } }] }];
      }

      return [{ AND: conditions }];
    },

    limpiar() {
      this.idBuscado = "";
      this.documentoBuscado = "";
      this.estadoBuscado = null;
      this.solicitudes = [];
      this.total = 0;
      this.page = 1;
      this.buscado = false;
    },

    abrirModalEstado(item) {
      this.solicitudSeleccionada = item;
      this.nuevoEstado = null;
      this.observacionesEstado = "";
      this.showModalEstado = true;
    },

    async confirmarCambioEstado(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.nuevoEstado) {
        this.$bvToast.toast("Debe seleccionar un nuevo estado", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.guardandoEstado = true;
      try {
        await clientesRepo.putEstadosSolicitudes({
          nombre_entidad: "entidad_solicitud",
          id_metadata: this.solicitudSeleccionada.id_metadata,
          id_version: 2,
          estado: this.nuevoEstado,
          observaciones: this.observacionesEstado || undefined,
        });

        this.$bvToast.toast(
          `Estado de la solicitud #${this.solicitudSeleccionada.id} cambiado a "${ESTADOS_MAP[this.nuevoEstado] || this.nuevoEstado}" correctamente.`,
          { title: "Éxito", variant: "success", solid: true }
        );

        this.showModalEstado = false;
        await this.buscarSolicitud();
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al cambiar el estado de la solicitud", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.guardandoEstado = false;
      }
    },

    async abrirModalProveedor(item) {
      this.solicitudSeleccionada = item;
      this.nuevoProveedorId = null;
      this.observacionesProveedor = "";
      this.showModalProveedor = true;
      await this.cargarProveedores();
    },

    async cargarProveedores() {
      this.cargandoProveedores = true;
      try {
        const params = {
          nombreEntidad: "entidad_proveedor",
          idVersion: 5,
          column: "id",
          type: "asc",
          size: 200,
          page: 1,
        };
        const where = [{ AND: [{ id: { greaterThan: 0 } }] }];
        const response = await configRepo.getMetaData(params, where, []);

        if (response && response.message && response.message.rows) {
          this.proveedoresOptions = [
            { value: null, text: "Seleccionar proveedor..." },
            ...response.message.rows.map((row) => ({
              value: row.data.id,
              text: row.data.nombre_proveedor || `Proveedor ${row.data.id}`,
            })),
          ];
        }
      } catch (err) {
        console.error("Error cargando proveedores:", err);
        this.proveedoresOptions = [{ value: null, text: "No se pudieron cargar los proveedores" }];
      } finally {
        this.cargandoProveedores = false;
      }
    },

    async confirmarReasignacion(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.nuevoProveedorId) {
        this.$bvToast.toast("Debe seleccionar un proveedor", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.guardandoProveedor = true;
      try {
        const fecha = new Date();
        await clientesRepo.putEstadosSolicitudes({
          nombre_entidad: "entidad_solicitud",
          id_metadata: this.solicitudSeleccionada.id_metadata,
          id_version: 2,
          id_proveedor: parseInt(this.nuevoProveedorId),
          fecha_asignacion: fecha,
          observaciones: this.observacionesProveedor || undefined,
        });

        const nombreNuevoProveedor =
          (this.proveedoresOptions.find((p) => p.value === this.nuevoProveedorId) || {}).text || this.nuevoProveedorId;

        this.$bvToast.toast(
          `Proveedor de la solicitud #${this.solicitudSeleccionada.id} reasignado a "${nombreNuevoProveedor}" correctamente.`,
          { title: "Éxito", variant: "success", solid: true }
        );

        this.showModalProveedor = false;
        await this.buscarSolicitud();
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al reasignar el proveedor", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.guardandoProveedor = false;
      }
    },

    variantEstado(estadoId) {
      const id = parseInt(estadoId);
      if (id === 1) return "secondary";
      if (id === 2) return "warning";
      if (id === 3) return "info";
      if (id === 5) return "primary";
      if (id === 6) return "primary";
      if (id === 7) return "success";
      if (id === 8 || id === 16) return "danger";
      if (id === 10) return "success";
      if (id === 11) return "dark";
      return "light";
    },

    async abrirModalDetalle(item) {
      this.solicitudSeleccionada = item;
      this.observacionDetalle = "";
      this.formDetalle = null;
      this.formDetalleOriginal = null;
      this.activados = {};
      this.showModalDetalle = true;
      this.cargandoDetalle = true;

      try {
        // Siempre recargar listas al abrir el modal
        await this.cargarListasModal();

        const response = await clientesRepo.consultaEntidadById({
          nombre_entidad: "entidad_solicitud",
          id_metadata: item.id_metadata,
        });

        let d = null;
        if (Array.isArray(response) && response.length > 0) {
          d = response[0].data || response[0];
        } else if (response && response.data) {
          d = response.data;
        } else {
          d = response;
        }

        const datosFormateados = {
          // IDs necesarios para guardar
          id: d.id,
          id_cliente: d.id_cliente,
          id_proveedor: d.id_proveedor,
          estado: d.estado,
          // Paciente — campos enteros casteados para que b-form-select haga match con value-field="id"
          nombre_completo: d.nombre_completo || d.Paciente || "",
          tipo_documento: d.tipo_documento != null ? parseInt(d.tipo_documento) : null,
          documento: d.documento,
          nacimiento: d.nacimiento ? d.nacimiento.slice(0, 10) : "",
          genero: d.genero != null ? parseInt(d.genero) : null,
          talla: d.talla != null ? parseInt(d.talla) : null,
          nacionalidad: d.nacionalidad || "Colombiana",
          telefono_1: d.telefono_1,
          telefono_2: d.telefono_2,
          email: d.email || "",
          eps: d.eps != null ? parseInt(d.eps) : null,
          cobertura: d.cobertura != null ? parseInt(d.cobertura) : null,
          afiliacion: d.afiliacion != null ? parseInt(d.afiliacion) : null,
          tipo_paciente: d.tipo_paciente || "",
          nivel_ingreso: d.nivel_ingreso || "",
          ips_paciente: d.ips_paciente || "",
          // Ubicación
          departamento: d.departamento || "",
          municipio: d.municipio || "",
          barrio: d.barrio || "",
          direccion: d.direccion || "",
          // Clínico
          diagnostico: d.diagnostico || "",
          cie_10_diagnostico: d.cie_10_diagnostico || "",
          dosis: d.dosis || "",
          horas_consumo_dia: d.horas_consumo_dia || "",
          tiempo_revision_medica: d.tiempo_revision_medica || "",
          doctor: d.doctor || "",
          lugar_entrega: d.lugar_entrega != null ? parseInt(d.lugar_entrega) : null,
          tipo_mascara: d.tipo_mascara != null ? parseInt(d.tipo_mascara) : null,
          hora_dosis: d.hora_dosis != null ? parseInt(d.hora_dosis) : (d.hora != null ? parseInt(d.hora) : null),
          // Elementos
          elementos: d.elementos || [],
          elementos_institucion: d.elementos_institucion || [],
          elementos_homologacion: d.elementos_homologacion || [],
          elementos_institucion_homologacion: d.elementos_institucion_homologacion || [],
          medio_administracion: d.medio_administracion || [],
          // Observaciones
          observaciones: d.observaciones || "",
          // Médico
          tipo_documento_medico: d.tipo_documento_medico || "",
          numero_documento_medico: d.numero_documento_medico || "",
          email_institucion: d.email_institucion || "",
          nombre_institucion: d.nombre_institucion || "",
          direccion_entrega: d.direccion_entrega || "",
          detalle_entrega: d.detalle_entrega || "",
          nombre_contacto: d.nombre_contacto || "",
          observacion_contacto: d.observacion_contacto || "",
        };
        this.formDetalle = { ...datosFormateados };
        this.formDetalleOriginal = { ...datosFormateados };

        // Log de diagnóstico para los campos más problemáticos
        console.log("[GestionSolicitudes] Campos críticos cargados:", {
          talla: datosFormateados.talla,
          tipo_mascara: datosFormateados.tipo_mascara,
          hora_dosis: datosFormateados.hora_dosis,
          lugar_entrega: datosFormateados.lugar_entrega,
          genero: datosFormateados.genero,
          cobertura: datosFormateados.cobertura,
          afiliacion: datosFormateados.afiliacion,
          "Lista talla (primeros 3):": this.listaTalla.slice(0, 4),
          "Lista mascara (primeros 3):": this.listaMascara.slice(0, 4),
        });
      } catch (err) {
        console.error("Error cargando detalle:", err);
        this.$bvToast.toast("Error al cargar el detalle de la solicitud", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.showModalDetalle = false;
      } finally {
        this.cargandoDetalle = false;
      }
    },

    async cargarListasModal() {
      try {
        const response = await configRepo.listasDesplegables({ nombre_entidad: "listados" });
        // Normaliza id a integer para garantizar match con b-form-select value-field="id"
        const find = (nombre) => {
          const rows = (response.find(i => i.data.nombre_lista === nombre) || { data: { rows: [] } }).data.rows;
          return rows.map(r => ({ ...r, id: r.id != null && !isNaN(parseInt(r.id)) ? parseInt(r.id) : r.id }));
        };
        this.listaTiposDocumento = [{ id: null, name: 'Seleccione...' }, ...find("Tipos_documento")];
        this.listaGenero       = [{ id: null, name: 'Seleccione...' }, ...find("genero")];
        this.listaTalla        = [{ id: null, name: 'Seleccione...' }, ...find("talla")];
        this.listaEps          = [{ id: null, name: 'Seleccione...' }, ...find("eps_colombia")];
        this.listaCobertura    = [{ id: null, name: 'Seleccione...' }, ...find("tipos_cobertura")];
        this.listaAfiliacion   = [{ id: null, name: 'Seleccione...' }, ...find("tipos_afiliacion")];
        this.listaLugar        = [{ id: null, name: 'Seleccione...' }, ...find("lugares_entrega")];
        this.listaMascara      = [{ id: null, name: 'Seleccione...' }, ...find("tipos_mascara_oxigeno")];
      } catch (err) {
        console.error("Error cargando listas del modal:", err);
      }
    },

    toggleCampo(campo) {
      this.$set(this.activados, campo, !this.activados[campo]);
      // Si se desactiva, restaurar el valor original
      if (!this.activados[campo]) {
        this.$set(this.formDetalle, campo, this.formDetalleOriginal[campo]);
      }
    },

    cerrarModalDetalle() {
      this.showModalDetalle = false;
      this.formDetalle = null;
      this.formDetalleOriginal = null;
      this.activados = {};
      this.observacionDetalle = "";
    },

    calcularFechaValoracion(dias) {
      if (!dias || parseInt(dias) <= 0) return null;
      const fecha = new Date();
      fecha.setDate(fecha.getDate() + parseInt(dias));
      const m = String(fecha.getMonth() + 1).padStart(2, "0");
      const d = String(fecha.getDate()).padStart(2, "0");
      return `${m}/${d}/${fecha.getFullYear()}`;
    },

    async guardarDetalle() {
      if (!this.observacionDetalle || this.observacionDetalle.trim().length === 0) {
        this.$bvToast.toast("Debe ingresar el motivo de la modificación", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }
      if (this.camposActivadosCount === 0) {
        this.$bvToast.toast("Debe activar al menos un campo para editar", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.guardandoDetalle = true;
      try {
        const f = this.formDetalleOriginal;
        const obsEditada = this.activados.observaciones
          ? this.formDetalle.observaciones
          : f.observaciones;
        const obsConJustificacion = `[ADMIN] ${this.observacionDetalle.trim()}` +
          (obsEditada ? ` | ${obsEditada}` : "");

        // Metadatos siempre requeridos
        const data = {
          estado: f.estado,
          id_cliente: f.id_cliente,
          id_proveedor: f.id_proveedor,
          observaciones: obsConJustificacion,
        };

        // Mapa de campos editables: [campo_api, campo_form, transformación]
        const camposEditables = [
          ["tipo_documento",                  "tipo_documento",       v => parseInt(v)],
          ["documento",                       "documento",            v => parseInt(v)],
          ["nombre_completo",                 "nombre_completo",      v => v],
          ["nacimiento",                      "nacimiento",           v => v || ""],
          ["genero",                          "genero",               v => parseInt(v)],
          ["talla",                           "talla",                v => parseInt(v)],
          ["nacionalidad",                    "nacionalidad",         v => v],
          ["telefono_1",                      "telefono_1",           v => parseInt(v) || 0],
          ["telefono_2",                      "telefono_2",           v => parseInt(v) || 0],
          ["email",                           "email",                v => v],
          ["eps",                             "eps",                  v => v],
          ["cobertura",                       "cobertura",            v => parseInt(v)],
          ["afiliacion",                      "afiliacion",           v => parseInt(v)],
          ["tipo_paciente",                   "tipo_paciente",        v => v],
          ["nivel_ingreso",                   "nivel_ingreso",        v => v || null],
          ["ips_paciente",                    "ips_paciente",         v => v || null],
          ["departamento",                    "departamento",         v => v],
          ["municipio",                       "municipio",            v => v],
          ["barrio",                          "barrio",               v => v],
          ["direccion",                       "direccion",            v => v],
          ["diagnostico",                     "diagnostico",          v => v],
          ["cie_10_diagnostico",              "cie_10_diagnostico",   v => v],
          ["dosis",                           "dosis",                v => v ? v.toString() : ""],
          ["horas_consumo_dia",               "horas_consumo_dia",    v => v ? v.toString() : ""],
          ["tiempo_revision_medica",          "tiempo_revision_medica", v => parseInt(v) || 0],
          ["doctor",                          "doctor",               v => v],
          ["lugar_entrega",                   "lugar_entrega",        v => parseInt(v)],
          ["tipo_mascara",                    "tipo_mascara",         v => parseInt(v) || 0],
          ["hora_dosis",                      "hora_dosis",           v => parseInt(v) || 0],
        ];

        // Agregar al data SOLO los campos que el admin activó
        camposEditables.forEach(([campoApi, campoForm, transform]) => {
          if (this.activados[campoForm]) {
            data[campoApi] = transform(this.formDetalle[campoForm]);
          }
        });

        // fecha_valoracion depende de tiempo_revision_medica
        if (this.activados.tiempo_revision_medica) {
          data.fecha_valoracion = this.calcularFechaValoracion(
            this.formDetalle.tiempo_revision_medica
          );
        }

        console.log("[GestionSolicitudes] data enviado a la API:", data);

        await clientesRepo.editarCamposAdministrativos({
          nombre_entidad: "entidad_solicitud",
          id_version: "2",
          id_metadata: f.id,
          data,
        });

        this.$bvToast.toast(`Solicitud #${f.id} actualizada correctamente.`, {
          title: "Éxito",
          variant: "success",
          solid: true,
        });
        this.cerrarModalDetalle();
        await this.buscarSolicitud();
      } catch (err) {
        console.error("Error guardando detalle:", err);
        const msg = err?.response?.data?.error?.message ||
          err?.response?.data?.mensaje ||
          "Error al guardar los cambios";
        this.$bvToast.toast(msg, { title: "Error", variant: "danger", solid: true });
      } finally {
        this.guardandoDetalle = false;
      }
    },
  },
};
</script>

<style scoped>
.filtros-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
.divnodata {
  text-align: center;
  padding: 40px 0;
}
.bannernodata {
  max-width: 300px;
  opacity: 0.5;
}
.badge-estado-negro {
  color: #000 !important;
}
</style>
